import { Link } from 'react-router-dom'
import { CalendarDays, Heart, ShieldCheck, Leaf, Home as HomeIcon } from 'lucide-react'
import { Section, SectionHeading, IconCircle, CTABand, SplitHero } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const categories = [
  { icon: CalendarDays, title: 'Care Planning Guides', body: 'Step-by-step help for families just beginning to explore in-home support options.' },
  { icon: Heart, title: 'Family Caregiver Tips', body: 'Practical advice on managing daily tasks while maintaining your own well-being.' },
  { icon: ShieldCheck, title: 'Home Safety', body: 'Simple modifications and habits that can significantly reduce the risk of falls.' },
  { icon: Leaf, title: "Dementia & Alzheimer's Support", body: 'Understanding behaviors and creating a calm, supportive environment at home.' },
  { icon: HomeIcon, title: 'Post-Hospital Recovery', body: 'What to expect and how to prepare for a smooth transition back home after surgery.' },
]

const articles = [
  { title: "How to start the care conversation with a parent who says they're fine", body: 'Gentle strategies for discussing support without infringing on independence.' },
  { title: 'Home Safety Checklist: Small changes that prevent big falls', body: 'A room-by-room guide to making the home safer for seniors.' },
  { title: 'Understanding the difference between Home Care and Home Health', body: 'Why non-medical support is a vital piece of the aging-in-place puzzle.' },
  { title: "The Family Caregiver's Guide to Avoiding Burnout", body: 'Practical tips for self-care while caring for a loved one.' },
  { title: 'What to pack for a planned hospital stay (and what to have ready for the return)', body: 'Being prepared for transitions of care.' },
  { title: 'Engaging activities for seniors with limited mobility', body: 'Keeping the mind and spirit active even when the body needs rest.' },
  { title: 'Navigating Dementia: Communication techniques that work', body: 'How to reduce frustration and connect more deeply.' },
  { title: 'Nutrition for Seniors: Simple meal ideas that taste like home', body: 'Healthy, easy-to-prepare options for everyday wellness.' },
]

export default function Resources() {
  return (
    <>
      <SplitHero
        eyebrow="Library"
        title="Helpful resources for Calgary families"
        lede="Navigating senior care can be overwhelming. We've gathered these guides and articles to help you make informed decisions for your loved ones."
        image="/assets/resources.jpg"
        imageAlt="A collection of books and a magnifying glass on a wooden table"
        actions={
          <>
            <Link to="/schedule-assessment" className="btn btn-primary">Schedule an In-Home Assessment</Link>
            <a href={PHONE_TEL} className="btn btn-secondary">Call {PHONE_DISPLAY}</a>
          </>
        }
      />

      {/* Categories */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Categories"
          title="Browse by topic"
          lede="We add new guides regularly. If you'd like a printed copy of any of these resources, just give us a call."
        />
        <div className="grid grid-3" style={{ marginTop: 48, gap: 24 }}>
          {categories.map((c) => (
            <div key={c.title} className="card-cream">
              <IconCircle icon={c.icon} />
              <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{c.title}</h3>
              <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Articles */}
      <Section tone="panel">
        <SectionHeading eyebrow="Articles" title="Recent guides and tips" />
        <div style={{ marginTop: 40, borderTop: '1px solid var(--color-line)' }}>
          {articles.map((a) => (
            <div key={a.title} style={{ padding: '32px 0', borderBottom: '1px solid var(--color-line)' }}>
              <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{a.title}</h3>
              <p style={{ margin: '8px 0 0 0', fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>{a.body}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 40, borderRadius: 8, background: 'var(--color-teal-900)', padding: 32, color: 'var(--color-cream)' }}>
          <p style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 600 }}>Need more specific information?</p>
          <p style={{ margin: '8px 0 0 0', fontSize: 19, lineHeight: 1.7, opacity: 0.9 }}>
            Our care coordinators are happy to discuss your specific situation and provide tailored recommendations.
          </p>
          <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            <Link to="/services" className="link-inline link-inline-on-dark">Explore our Services</Link>
            <Link to="/how-it-works" className="link-inline link-inline-on-dark">How the Process Works</Link>
          </div>
        </div>
      </Section>

      <CTABand />
    </>
  )
}
